/**
 * Registration Form Overlay
 *
 * The overlay markup lives inside the shared `drawer` snippet, so
 * `drawer-component` already owns opening, closing, escape handling and
 * focus trapping. This script layers the two-view behavior on top:
 *   - default: the registration form
 *   - success: the confirmation message
 *
 * Elements belonging to a view are tagged with `data-view="default|success"`.
 * `showView()` flips their `hidden` attribute to reveal one view at a time.
 *
 * NOTE: real form submission isn't wired yet (see the TO DO in the snippet),
 * so submitting is intercepted and simply switches to the success view.
 *
 * Article-name auto-fill: once a brand and an article number (the variant SKU)
 * are entered, we query Shopify's native predictive-search endpoint for that
 * SKU and fill the read-only article-name field with the matching product's
 * title. Brand narrows the result to the product whose `vendor` matches the
 * selected brand. The Storefront API can't search by SKU, so predictive search
 * (with `variants.sku` added to its searchable fields) is the token-free path.
 */

const METHOD = "POST";
const PRODUCT_REGISTRATION_API_ENDPOINT = 'https://www.katadynforthemission.com/api/product-registration';

// Shown when the request never reached the server (network/CORS failure), so
// there is no response body to read a message from.
const GENERIC_ERROR_MESSAGE = 'Something went wrong and your message could not be sent. Please try again later.';

class RegistrationFormOverlay {
  constructor(overlayHash = 'product-registration-form') {
    this.overlayHash = overlayHash;

    this.overlay = document.querySelector(
      `drawer-component[data-overlay-hash="${this.overlayHash}"]`
    );

    if (!this.overlay) return;

    this.form = this.overlay.querySelector('#registration-form');
    this.closeButton = this.overlay.querySelector('[data-close-button]');
    // Queried from the overlay, not the form: the button lives in the drawer
    // footer and is only associated with the form via `form="registration-form"`.
    this.submitButton = this.overlay.querySelector('[data-submit-button]');
    this.views = this.overlay.querySelectorAll('[data-view]');
    this.errorEl = this.overlay.querySelector('[data-contact-error]');
    this.inner = this.overlay.querySelector('.drawer__inner');
    // Fields are looked up by `name`, not `id`: the name is the contract shared
    // with the API (its field errors are keyed by it), whereas the id only
    // exists to bind the label and is free to be renamed.
    this.countrySelect = this.form?.querySelector('[name="country"]');

    this.brandSelect = this.form?.querySelector('[name="brand"]');
    this.articleNumberInput = this.form?.querySelector('[name="article_number"]');
    this.articleNameInput = this.form?.querySelector('[name="article_name"]');
    this.productIdInput = this.form?.querySelector('[name="product_id"]');
    this.articleNameStatus = this.overlay.querySelector('#article-name-status');
    this.articleLookupController = null;

    this.removeCountryDivider();
    this.bindEvents();
    // Required fields start empty, so the button has to start disabled. Without
    // this it renders enabled until the first `input` event corrects it.
    this.updateSubmitState();
  }

  // `all_country_option_tags` renders a `---` option that can't be removed
  // within Liquid, so we remove it here to get a clean country list:
  removeCountryDivider() {
    const secondOption = this.countrySelect?.options[1];
    if (secondOption?.value === '---') {
      secondOption.remove();
    }
  }

  bindEvents() {
    this.form?.addEventListener('submit', this.handleSubmit.bind(this));
    this.form?.addEventListener('input', this.updateSubmitState.bind(this));
    // Backend field errors describe the value that was submitted, so they stop
    // being true the moment the user edits that field. `input` covers selects
    // and checkboxes too, so every control clears its own error as it's fixed.
    this.form?.addEventListener('input', this.handleFieldEdit.bind(this));

    this.closeButton?.addEventListener('click', this.handleClose.bind(this));
    document.addEventListener('overlay:open', this.handleOverlayOpen.bind(this));
    document.addEventListener('overlay:close', this.handleOverlayClose.bind(this));

    // Both fields feed the same lookup: SKU + brand together. The article
    // number fires on `blur` so we look up once the user leaves the field,
    // not on every keystroke; the select fires `change` on choose, which
    // covers entering the article number before picking the brand.
    const lookup = this.handleArticleLookup.bind(this);
    this.brandSelect?.addEventListener('change', lookup);
    this.articleNumberInput?.addEventListener('blur', lookup);
  }

  // The trigger is <a href="#registration-form">, but the <form> is also
  // id="registration-form", so the browser's native fragment jump scrolls the
  // inner to the form on open. This fires after that jump and resets it.
  handleOverlayOpen(event) {
    if (event.detail?.overlayId === this.overlay.id && this.inner) {
      this.inner.scrollTop = 0;
    }
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (this.isSubmitting) return;

    this.hideError();
    this.clearFieldErrors();

    const formData = new FormData(this.form);

    try {
      this.setSubmitting(true);

      const response = await fetch(PRODUCT_REGISTRATION_API_ENDPOINT, {
        method: METHOD,
        body: formData,
      });

      if (!response.ok) {
        await this.handleErrorResponse(response);
        return;
      }

      this.showView('success');
    } catch (error) {
      // A rejected fetch (network/CORS) throws a TypeError with no response
      // body to read; handleErrorResponse already covers whatever the server
      // actually sent back.
      console.error('[ProductRegistrationFormOverlay] Submission failed:', error);
      this.showError(GENERIC_ERROR_MESSAGE);
    } finally {
      this.setSubmitting(false);
    }
  }

  // Route a non-2xx response. The API returns Zod's flattenError().fieldErrors
  // as { errors: { field: [msg] } }: each field's message is rendered beneath
  // its input, and anything without a matching field — a top-level message or
  // an unknown key — falls back to the footer so nothing is silently dropped.
  async handleErrorResponse(response) {
    const contentType = response.headers.get('content-type') || '';

    if (!contentType.includes('application/json')) {
      const text = await response.text();
      this.showError(text.trim() || GENERIC_ERROR_MESSAGE);
      return;
    }

    let data;
    try {
      data = await response.json();
    } catch {
      this.showError(GENERIC_ERROR_MESSAGE);
      return;
    }

    const { shownCount, unmatched } =
      data.errors && typeof data.errors === 'object'
        ? this.showFieldErrors(data.errors)
        : { shownCount: 0, unmatched: [] };

    // Form-level messages and any field with no slot go to the footer.
    const footerMessage = [data.message, data.error, ...unmatched]
      .filter(Boolean)
      .join('\n');

    if (footerMessage) this.showError(footerMessage);
    else if (!shownCount) this.showError(GENERIC_ERROR_MESSAGE);
  }

  // Fill each field's inline error slot, keyed by the field `name`. Returns how
  // many were shown and any messages whose field had no slot on the page.
  showFieldErrors(fieldErrors) {
    let shownCount = 0;
    const unmatched = [];
    let firstInvalid = null;

    Object.entries(fieldErrors).forEach(([field, messages]) => {
      const message = [].concat(messages).filter(Boolean).join(' ');
      if (!message) return;

      const errorEl = this.form?.querySelector(`[data-error-for="${field}"]`);
      const input = this.form?.querySelector(`[name="${field}"]`);

      if (!errorEl || !input) {
        unmatched.push(message);
        return;
      }

      errorEl.textContent = message;
      errorEl.hidden = false;
      this.markInvalid(input, errorEl);
      shownCount += 1;
      firstInvalid ||= input;
    });

    firstInvalid?.focus();
    return { shownCount, unmatched };
  }

  // Flag the control for assistive tech and point it at its message, without
  // clobbering any description the field already references.
  markInvalid(input, errorEl) {
    input.setAttribute('aria-invalid', 'true');
    const describedBy = (input.getAttribute('aria-describedby') || '')
      .split(' ')
      .filter((token) => token && token !== errorEl.id);
    describedBy.push(errorEl.id);
    input.setAttribute('aria-describedby', describedBy.join(' '));
  }

  clearFieldErrors() {
    this.form
      ?.querySelectorAll('[data-error-for]')
      .forEach((errorEl) => this.clearFieldError(errorEl.dataset.errorFor));
  }

  clearFieldError(field) {
    const errorEl = this.form?.querySelector(`[data-error-for="${field}"]`);
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    const input = this.form?.querySelector(`[name="${field}"]`);
    if (!input) return;

    input.removeAttribute('aria-invalid');
    const describedBy = (input.getAttribute('aria-describedby') || '')
      .split(' ')
      .filter((token) => token && token !== errorEl?.id);
    if (describedBy.length) input.setAttribute('aria-describedby', describedBy.join(' '));
    else input.removeAttribute('aria-describedby');
  }

  handleFieldEdit(event) {
    const field = event.target?.name;
    if (field) this.clearFieldError(field);
  }

  // Owns the button while a request is in flight, so `updateSubmitState()` has
  // to stand down until it's over. Releasing hands control back by re-deriving
  // the disabled state from validity rather than blindly enabling.
  setSubmitting(isSubmitting) {
    this.isSubmitting = isSubmitting;
    if (!this.submitButton) return;

    this.submitButton.setAttribute('aria-busy', String(isSubmitting));

    if (isSubmitting) this.submitButton.disabled = true;
    else this.updateSubmitState();
  }

  // Mirror the form's native validity onto the standalone submit button.
  // The button is associated via `form="registration-form"` but lives outside the
  // <form>, so it isn't disabled automatically when required fields are empty.
  // The article name is checked separately: it's `readonly`, and readonly
  // inputs are barred from constraint validation, so `required` is ignored.
  updateSubmitState() {
    if (!this.submitButton || !this.form || this.isSubmitting) return;
    const hasArticleName = Boolean(this.articleNameInput?.value.trim());
    this.submitButton.disabled = !this.form.checkValidity() || !hasArticleName;
  }

  handleClose() {
    this.overlay.close();
  }

  handleOverlayClose(event) {
    if (event.detail?.overlayId === this.overlay.id) {
      this.showView('default');
    }
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
  }

  showView(view) {
    this.views.forEach((el) => {
      el.hidden = el.dataset.view !== view;
    });
  }

  // Resolves the article name from the entered SKU, scoped to the chosen brand.
  async handleArticleLookup() {
    const sku = this.articleNumberInput?.value.trim();
    const brand = this.getSelectedBrandName();

    // A unique product needs both the SKU and the brand; return until we have both.
    if (!sku || !brand) {
      this.clearArticleMatch();
      this.setStatus('');
      return;
    }

    this.setStatus('Looking up article…');

    // Cancel any in-flight lookup so the most recent input always wins.
    this.articleLookupController?.abort();
    this.articleLookupController = new AbortController();

    try {
      const product = await this.fetchProductBySku(
        sku,
        brand,
        this.articleLookupController.signal
      );

      if (product) {
        this.setArticleName(product.title);
        this.setProductId(product.id);
        this.setStatus('');
      } else {
        this.clearArticleMatch();
        this.setStatus(`No ${brand} product found for article number “${sku}”. Please try again.`);
      }
    } catch (error) {
      if (error.name === 'AbortError') return; // superseded by a newer lookup
      this.clearArticleMatch();
      this.setStatus('Could not look up the article name. Please try again.');
    }
  }

  // The <option> label is the brand's display name, which matches the product
  // vendor, so we read the label here. We gate on the placeholder's `disabled`
  // attribute rather than the option value: the value carries the metaobject
  // key, which isn't guaranteed to render, whereas the label always does.
  getSelectedBrandName() {
    const option = this.brandSelect?.selectedOptions?.[0];
    if (!option || option.disabled) return '';
    return option.text.trim();
  }

  async fetchProductBySku(sku, brand, signal) {
    const base = window.routes.predictive_search_url;

    const params = new URLSearchParams({
      q: sku,
      'resources[type]': 'product',
      'resources[limit]': '10',
      // Restrict matching to the SKU field so titles/tags can't cause false hits.
      'resources[options][fields]': 'variants.sku',
    });

    const response = await fetch(`${base}.json?${params}`, { signal });
    if (!response.ok) {
      throw new Error(`Predictive search failed: ${response.status}`);
    }

    const data = await response.json();
    const products = data.resources?.results?.products ?? [];

    // Brand was chosen explicitly, so only trust a vendor-matched result.
    return (
      products.find(
        (product) => product.vendor?.trim().toLowerCase() === brand.toLowerCase()
      ) ?? null
    );
  }

  // The article name and the product id are two halves of one match, so they are
  // always cleared together — otherwise a stale id from an earlier lookup would
  // be submitted alongside an empty article name.
  clearArticleMatch() {
    this.setArticleName('');
    this.setProductId('');
  }

  // Programmatic value changes don't fire `input`, so re-sync the button here.
  setArticleName(value) {
    if (this.articleNameInput) this.articleNameInput.value = value;
    this.updateSubmitState();
  }

  setProductId(value) {
    if (this.productIdInput) this.productIdInput.value = value;
  }

  setStatus(message) {
    if (!this.articleNameStatus) return;
    this.articleNameStatus.textContent = message;
    this.articleNameStatus.hidden = message === '';
  }

  static init() {
    new RegistrationFormOverlay();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', RegistrationFormOverlay.init);
} else {
  RegistrationFormOverlay.init();
}